import {AxiosError} from "axios";
import {useEffect, useState} from 'react'
import Button from 'react-bootstrap/Button';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {DLPApi, PolicyViolationAction} from "../api/admin/generated";
import {DialogCloseCause} from "../common/Enums";
import {RenderAlert} from "../common/RenderAlert";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {logger} from "../utils/Logger";
import {StringUtils} from "../utils/StringUtils";
import {Utils} from "../utils/Utils";

/*
 * REST api for accessing the back-end
 */
const dlpApi = new DLPApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface DLPAddPatternDialogProps {
    editName? : string
    onClose : OnClose
}

export const DLPPatternDialog = ({editName, onClose} : DLPAddPatternDialogProps ) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Pattern name
     */
    const [name, setName] = useState<string>(editName??"");
    /**
     * Pattern description
     */
    const [description, setDescription] = useState<string>("");
    /**
     * Pattern RegEx
     */
    const [regEx, setRegEx] = useState<string>("");
    /**
     * All available validators
     */
    const [validators, setValidators] = useState<string[]>([]);
    /**
     * Pattern validator
     */
    const [validator, setValidator] = useState<string>("");
    /**
     * Pattern Threshold
     */
    const [threshold, setThreshold] = useState<number>(0);
    /**
     * Pattern action
     */
    const [action, setAction] = useState<PolicyViolationAction>(PolicyViolationAction.Warn);
    /**
     * Delay Evaluation
     */
    const [delayEvaluation, setDelayEvaluation] = useState<boolean>(false);
    /**
     * All Match Filters
     */
    const [matchFilters, setMatchFilters] = useState<string[]>([]);
    /**
     * Match Filter
     */
    const [matchFilter, setMatchFilter] = useState<string>("");
    /**
     * Set to true while adding the pattern to show a spinner on the import button
     */
    const [saving, setSaving] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {spinnerStateCounter, setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    useEffect(() =>
    {
        logger.debug("Load validators")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        dlpApi.dLPControllerGetValidators()
            .then(response =>
            {
                const validators = response.data.map(v => v.name);
                // add empty option
                validators.push("");

                setValidators(validators);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting validators", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() =>
    {
        logger.debug("Load match filters")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        dlpApi.dLPControllerGetMatchFilters()
            .then(response =>
            {
                const matchFilters = response.data.map(v => v.name);
                // add empty option
                matchFilters.push("");

                setMatchFilters(matchFilters);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting match filters", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() =>
    {
        // only load existing settings if editing a pattern
        if (editName === undefined) {
            return
        }

        logger.debug("Load pattern")

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        dlpApi.dLPControllerGetPolicyPattern(editName)
            .then(response => {
                setDescription(response.data.description);
                setRegEx(response.data.regEx);
                setValidator(response.data.validator);
                setThreshold(response.data.threshold);
                setAction(response.data.action as PolicyViolationAction);
                setDelayEvaluation(response.data.delayEvaluation);
                setMatchFilter(response.data.matchFilter);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting pattern node", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        setSpinnerStateCounter,
        editName,
        t
    ]);

    const saveChanges = () =>
    {
        setSaving(true);

        const callback = editName === undefined ? async () => {
            return await dlpApi.dLPControllerAddPolicyPattern(
                name,
                regEx,
                action,
                delayEvaluation,
                description,
                "",
                validator,
                threshold,
                matchFilter)
        } :
            async () => {
                return await dlpApi.dLPControllerUpdatePolicyPattern(
                    name,
                    regEx,
                    action,
                    delayEvaluation,
                    description,
                    "",
                    validator,
                    threshold,
                    matchFilter)
            }

        callback().then(() =>
            {
                onClose(DialogCloseCause.Succes);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error saving pattern", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setSaving(false);
            });
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()} size="xl" fullscreen="lg-down" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{editName === undefined ? t("dlp.add-pattern") : t("dlp.edit-pattern")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row flex-grow-1">
                    <main className="col d-flex flex-column overflow-auto modal-main-body">
                        <RenderAlert message={errorMessage} setMessage={setErrorMessage}
                                     tooltipPlacement={"bottom"}/>
                        <Form>
                            <FloatingLabel label={t("dlp.name")} className="text-secondary mb-3">
                                <Form.Control type="text" placeholder={t("dlp.name")} value={name}
                                              onChange={e => setName(e.target.value)} disabled={editName !== undefined}/>
                            </FloatingLabel>

                            <FloatingLabel label={t("dlp.description")} className="text-secondary mb-3">
                                <Form.Control type="text" placeholder={t("dlp.description")} value={description}
                                              onChange={e => setDescription(e.target.value)}/>
                            </FloatingLabel>

                            <FloatingLabel label={t("dlp.regular-expression")} className="text-secondary mb-3">
                                <Form.Control type="text" placeholder={t("dlp.regular-expression")} value={regEx}
                                              onChange={e => setRegEx(e.target.value)}/>
                            </FloatingLabel>

                            <FloatingLabel label={t("dlp.threshold")} className="text-secondary mb-3">
                                <Form.Control type="number" min={0} placeholder={t("dlp.threshold")} value={threshold}
                                              onChange={e => setThreshold(Number(e.target.value))}/>
                            </FloatingLabel>

                            <Form.Group className="mb-3">
                                <Form.Label className="text-secondary">{t("dlp.validator")}</Form.Label>
                                    <Form.Select size="sm" value={validator} onChange={(e) => setValidator(e.target.value)}>
                                    {validators?.map(v => (
                                        <option key={v} value={v}>{v}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="text-secondary">{t("dlp.action")}</Form.Label>
                                <Form.Select size="sm"
                                             value={action}
                                             onChange={(e) => setAction(Utils.stringToEnum(PolicyViolationAction, e.target.value as PolicyViolationAction))}>
                                    <option value="WARN">{t("dlp.warn")}</option>
                                    <option value="MUST_ENCRYPT">{t("dlp.must-encrypt")}</option>
                                    <option value="QUARANTINE">{t("dlp.quarantine")}</option>
                                    <option value="BLOCK">{t("dlp.block")}</option>
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label className="text-secondary">{t("dlp.match-filter")}</Form.Label>
                                <Form.Select size="sm" value={matchFilter} onChange={(e) => setMatchFilter(e.target.value)}>
                                    {matchFilters?.map(mf => (
                                        <option key={mf} value={mf}>{mf}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <Form.Check
                                className="text-secondary"
                                type="switch"
                                label={t("dlp.delayed-evaluation")}
                                checked={delayEvaluation}
                                onChange={(e) => setDelayEvaluation(e.target.checked)}
                            />
                        </Form>
                    </main>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={saveChanges}
                        disabled={StringUtils.isBlank(name) || StringUtils.isBlank(regEx) || spinnerStateCounter > 0}>
                    {saving && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {saving ? t("common.saving") : t("common.save")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
